import { Data, Node } from 'unist';
import { Properties } from 'hast';
import { Components } from 'react-markdown';
import { Directives } from 'mdast-util-directive';
import { visit } from 'unist-util-visit';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Copy } from 'react-bootstrap-icons';
import classNames from 'classnames';

import {
	AI_RESPONSE_SOURCE_IDS,
	FollowUpQuestionSuggestion,
	individualResponseSourceOptions,
	MessageEventDataPostAnswerPayload,
	MessageEventDataSuggestionsPayload,
} from '@/lib/models';
import { AiExampleQuestion, AiImage, AiRateModal } from '@/components';
import { useCopyTextToClipboard } from '@/hooks';
import { isAbsoluteUrl } from './url-utils';

/* ----------------------------------------- */
/* Default tokens */
/* ----------------------------------------- */
enum DEFAULT_DIRECTIVE_NAMES {
	CONTAINER = 'containerDirective',
	LEAF = 'leafDirective',
	TEXT = 'textDirective',
}
const defaultDirectiveNames = new Set([
	DEFAULT_DIRECTIVE_NAMES.CONTAINER,
	DEFAULT_DIRECTIVE_NAMES.LEAF,
	DEFAULT_DIRECTIVE_NAMES.TEXT,
]);

/* ----------------------------------------- */
/* Custom tokens */
/* ----------------------------------------- */
enum CUSTOM_DIRECTIVE_NAMES {
	SOURCE = 'source',
	SOURCE_DIVIDER = 'sourceDivider',
	SUGGESTIONS = 'suggestions',
	SHOW_MORE = 'showMore',
	POST_ANSWER = 'postAnswer',
}
const customDirectiveNames = new Set([
	CUSTOM_DIRECTIVE_NAMES.SOURCE,
	CUSTOM_DIRECTIVE_NAMES.SOURCE_DIVIDER,
	CUSTOM_DIRECTIVE_NAMES.SUGGESTIONS,
	CUSTOM_DIRECTIVE_NAMES.SHOW_MORE,
	CUSTOM_DIRECTIVE_NAMES.POST_ANSWER,
]);

/* ----------------------------------------- */
/* Type check helpers */
/* ----------------------------------------- */
type CustomDirective = Directives & {
	name: CUSTOM_DIRECTIVE_NAMES;
	attributes?: Record<string, string> | null;
};

function isDefaultDirective(node: Node): node is Directives {
	return defaultDirectiveNames.has(node.type as DEFAULT_DIRECTIVE_NAMES);
}

function isCustomDirective(node: Node): node is CustomDirective {
	return isDefaultDirective(node) && customDirectiveNames.has(node.name as CUSTOM_DIRECTIVE_NAMES);
}

/* ----------------------------------------- */
/* Hypertext abstract syntax tree helper */
/* ----------------------------------------- */
function toHast(node: Node, kind: CUSTOM_DIRECTIVE_NAMES, props: Properties = {}) {
	const data = (node.data = node.data ?? {}) as Data & { hName?: string; hProperties?: Properties };

	data.hName = 'div';
	data.hProperties = { ...(data.hProperties ?? {}), 'data-remark': kind, ...props };
}

/* ----------------------------------------- */
/* Remark token registration helper */
/* ----------------------------------------- */
export function remarkCustomTokens() {
	return (tree: Node) => {
		visit(tree, isCustomDirective, (node: CustomDirective) => {
			const nodeAttributes = node.attributes ?? {};

			switch (node.name) {
				case CUSTOM_DIRECTIVE_NAMES.SOURCE:
					toHast(node, CUSTOM_DIRECTIVE_NAMES.SOURCE, { payload: nodeAttributes.payload ?? '' });
					break;
				case CUSTOM_DIRECTIVE_NAMES.SOURCE_DIVIDER:
					toHast(node, CUSTOM_DIRECTIVE_NAMES.SOURCE_DIVIDER);
					break;
				case CUSTOM_DIRECTIVE_NAMES.SUGGESTIONS:
					toHast(node, CUSTOM_DIRECTIVE_NAMES.SUGGESTIONS, { payload: nodeAttributes.payload ?? '' });
					break;
				case CUSTOM_DIRECTIVE_NAMES.SHOW_MORE:
					toHast(node, CUSTOM_DIRECTIVE_NAMES.SHOW_MORE, { payload: nodeAttributes.payload ?? '' });
					break;
				case CUSTOM_DIRECTIVE_NAMES.POST_ANSWER:
					toHast(node, CUSTOM_DIRECTIVE_NAMES.POST_ANSWER, { payload: nodeAttributes.payload ?? '' });
					break;
				default: {
					const _exhaustiveCheck: never = node.name;
					return _exhaustiveCheck;
				}
			}
		});
	};
}

/* ----------------------------------------- */
/* Remark component parser */
/* ----------------------------------------- */
export interface RemarkComponentsConfig {
	onSuggestionClick(suggestion: FollowUpQuestionSuggestion): void;
	getAiConversationId(): string;
	readOnly?: boolean;
}

export const getRemarkComponents = ({
	onSuggestionClick,
	getAiConversationId,
	readOnly = false,
}: RemarkComponentsConfig) => {
	const comps: Components = {
		div: ({ node, children, ...rest }) => {
			const componentName = (String(node?.properties['data-remark']) as CUSTOM_DIRECTIVE_NAMES) ?? '';

			if (componentName === CUSTOM_DIRECTIVE_NAMES.SOURCE) {
				const payloadAttr = String(node?.properties['payload'] ?? '');
				const parsedPayload = payloadAttr as AI_RESPONSE_SOURCE_IDS;

				return <RemarkSource aiResponseSourceId={parsedPayload} />;
			}

			if (componentName === CUSTOM_DIRECTIVE_NAMES.SOURCE_DIVIDER) {
				return <RemarkSourceDivider />;
			}

			if (componentName === CUSTOM_DIRECTIVE_NAMES.SUGGESTIONS) {
				const payloadAttr = String(node?.properties['payload'] ?? '');
				let parsedPayload: MessageEventDataSuggestionsPayload | null = null;

				try {
					parsedPayload = JSON.parse(decodeURIComponent(payloadAttr));
				} catch {
					parsedPayload = null;
				}

				if (!parsedPayload) {
					return null;
				}

				return (
					<RemarkSuggestions
						suggestions={parsedPayload.suggestions}
						onSuggestionClick={onSuggestionClick}
						readOnly={readOnly}
					/>
				);
			}

			if (componentName === CUSTOM_DIRECTIVE_NAMES.SHOW_MORE) {
				const payloadAttr = String(node?.properties['payload'] ?? '');
				const parsedPayload = payloadAttr as unknown;

				return <RemarkShowMore props={parsedPayload} />;
			}

			if (componentName === CUSTOM_DIRECTIVE_NAMES.POST_ANSWER) {
				const payloadAttr = String(node?.properties['payload'] ?? '');
				let parsedPayload: MessageEventDataPostAnswerPayload | null = null;

				try {
					parsedPayload = JSON.parse(decodeURIComponent(payloadAttr));
				} catch {
					parsedPayload = null;
				}

				if (!parsedPayload) {
					return null;
				}

				if (readOnly) {
					return null;
				}

				return (
					<RemarkPostAnswer
						conversationQueryId={parsedPayload.conversationQueryId}
						hasRating={parsedPayload.hasRating}
					/>
				);
			}

			return <div {...rest}>{children}</div>;
		},
		img: ({ alt, src, node }) => {
			const pos = node?.position;
			const offset: number | undefined = pos?.start?.offset;
			const lineCol: string | undefined = pos?.start && `${pos.start.line}:${pos.start.column}`;
			const uid =
				offset != null
					? `img::${src ?? ''}::${offset}`
					: lineCol
					? `img::${src ?? ''}::${lineCol}`
					: `img::${src ?? ''}`;

			return <AiImage key={uid} alt={alt ?? ''} src={src ?? ''} />;
		},
		a: ({ href, children }) => {
			if (!href) {
				return <></>;
			}

			const isExternal = isAbsoluteUrl(href);
			const url = isExternal ? new URL(href) : new URL(href, window.location.origin);

			if (!isExternal) {
				url.searchParams.set('ref', 'chat');

				if (getAiConversationId()) {
					url.searchParams.set('aiConversationIdNav', getAiConversationId());
				}
			}

			return (
				<a href={url.toString()} target="_blank" rel="noopener noreferrer">
					{children}
				</a>
			);
		},
	};

	return comps;
};

/* ----------------------------------------- */
/* Custom remark compoents */
/* ----------------------------------------- */
const RemarkSource = React.memo(({ aiResponseSourceId }: { aiResponseSourceId: AI_RESPONSE_SOURCE_IDS }) => {
	const sourceOption = useMemo(
		() => individualResponseSourceOptions.find((option) => option.aiResponseSourceId === aiResponseSourceId),
		[aiResponseSourceId]
	);

	if (!sourceOption) {
		return null;
	}

	return (
		<div className="mb-5 d-flex align-items-center">
			<sourceOption.icon size={24} className="mr-2 text-primary" />
			<h3 className="mb-0 font-weight-semi-bold">From {sourceOption.title}</h3>
		</div>
	);
});

const RemarkSourceDivider = React.memo(() => {
	return <hr className="my-5" />;
});

const RemarkSuggestions = React.memo(
	({
		suggestions,
		onSuggestionClick,
		readOnly,
	}: {
		suggestions: FollowUpQuestionSuggestion[];
		onSuggestionClick(suggestion: FollowUpQuestionSuggestion): void;
		readOnly?: boolean;
	}) => {
		if (readOnly || suggestions.length <= 0) {
			return null;
		}

		return (
			<div className="ignore-copy">
				<h4 className="mb-3 font-weight-semi-bold">Recommended follow-up questions:</h4>
				{suggestions.map((suggestion, index) => {
					const isLast = suggestions.length - 1 === index;

					return (
						<AiExampleQuestion
							className={classNames({ 'mb-2': !isLast })}
							key={index}
							question={suggestion.suggestion}
							onClick={() => {
								onSuggestionClick(suggestion);
							}}
						/>
					);
				})}
			</div>
		);
	}
);

const RemarkShowMore = React.memo(({ props }: { props: unknown }) => {
	useEffect(() => {
		console.log('showMoreProps', props);
	}, [props]);

	return <div className="ignore-copy"></div>;
});

const RemarkPostAnswer = React.memo(
	({ conversationQueryId, hasRating }: { conversationQueryId: string; hasRating: boolean }) => {
		const { copyHtmlToClipboard } = useCopyTextToClipboard();

		const [internalHasRating, setInternalHasRating] = useState(hasRating);
		const [showRateModal, setShowRateModal] = useState(false);

		const handleRateModalSave = useCallback(() => {
			setShowRateModal(false);
			setInternalHasRating(true);
		}, []);

		const getCopyableAnswerHTML = (startNode: Node | null): string | null => {
			const startEl =
				startNode instanceof Element ? startNode : ((startNode as unknown) as Element)?.parentElement ?? null;

			if (!startEl) return null;

			const container = startEl.closest('.chat-answer-to-copy');
			if (!container) return null;

			const clone = container.cloneNode(true) as HTMLElement;

			clone.querySelectorAll('.ignore-copy').forEach((el) => el.remove());

			return clone.innerHTML;
		};

		return (
			<>
				<AiRateModal
					conversationQueryId={conversationQueryId}
					show={showRateModal}
					onHide={() => {
						setShowRateModal(false);
					}}
					onSave={handleRateModalSave}
				/>

				<div className="mt-3 d-flex justify-content-end align-items-center ignore-copy">
					<Button
						size="sm"
						variant="outline-primary"
						className="d-flex align-items-center mr-2"
						style={{ padding: 12 }}
						onClick={({ currentTarget }) => {
							copyHtmlToClipboard(getCopyableAnswerHTML(currentTarget) ?? '');
						}}
					>
						<Copy size={16} className="d-flex flex-shrink-0" />
					</Button>
					<Button
						size="sm"
						variant={internalHasRating ? 'primary' : 'outline-primary'}
						onClick={() => setShowRateModal(true)}
						disabled={internalHasRating}
					>
						{internalHasRating ? 'Rating Recieved' : 'Rate Answer'}
					</Button>
				</div>
			</>
		);
	}
);
